/**
 * DocSidebarTagsSection Component
 * 
 * Displays the tags section of the document sidebar.
 * Handles tag display, addition, removal, and suggestions.
 */

import { useState, useRef, useEffect, useMemo } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tag as TagIcon, X, Plus } from "lucide-react";
import { useAddTag, useRemoveTag } from "../api/useDocTags";
import { useTagSuggestions } from "../api/useTagSuggestions";
import { useDebounce } from "@/lib/hooks/useDebounce";
import { useLanguage } from "@/i18n/useLanguage";
import { getUITranslation } from "@/lib/views/labels/translations";

type DocSidebarTagsSectionProps = {
  doctype: string;
  name: string;
  tags: string[];
  editable?: boolean;
};

export function DocSidebarTagsSection({
  doctype,
  name,
  tags,
  editable = false,
}: DocSidebarTagsSectionProps) {
  const { language } = useLanguage();
  const [tagInput, setTagInput] = useState("");
  const [showTagSuggestions, setShowTagSuggestions] = useState(false);
  const tagInputRef = useRef<HTMLInputElement>(null);
  const tagSuggestionsRef = useRef<HTMLDivElement>(null);

  const uiLabels = useMemo(() => {
    const getUI = (key: string, fallback: string) => getUITranslation(key, language) || fallback;
    return {
      addTag: getUI('add_tag', 'Add tag...'),
    };
  }, [language]);

  const addTagMutation = useAddTag(doctype, name);
  const removeTagMutation = useRemoveTag(doctype, name);

  // Debounce tag input for suggestions
  const debouncedTagInput = useDebounce(tagInput, 300);

  // Fetch tag suggestions
  const {
    data: tagSuggestions = [],
    isLoading: isLoadingTags,
    error: tagSuggestionsError,
  } = useTagSuggestions(doctype, debouncedTagInput, {
    enabled: editable,
    isOpen: showTagSuggestions,
    minLength: 0, // Allow fetching on focus even with empty input
  });

  // Log errors for debugging
  useEffect(() => {
    if (tagSuggestionsError) {
      console.error("Tag suggestions error:", tagSuggestionsError);
    }
  }, [tagSuggestionsError]);

  const handleAddTag = (tagToAdd?: string) => {
    const tag = tagToAdd || tagInput.trim();
    if (tag && !tags.includes(tag)) {
      addTagMutation.mutate(tag, {
        onSuccess: () => {
          setTagInput("");
          setShowTagSuggestions(false);
        },
      });
    }
  };

  const handleRemoveTag = (tag: string) => {
    removeTagMutation.mutate(tag);
  };

  // Handle clicking outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        tagSuggestionsRef.current &&
        !tagSuggestionsRef.current.contains(event.target as Node) &&
        tagInputRef.current &&
        !tagInputRef.current.contains(event.target as Node)
      ) {
        setShowTagSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  if (tags.length === 0 && !editable) {
    return null;
  }

  return (
    <>
      <div className="space-y-3 sm:space-y-4">
        {/* Tags */}
        {(tags.length > 0 || editable) && (
          <div className="space-y-2.5">
            <div className="flex items-center gap-2 text-[11px] font-medium text-muted-foreground/80 uppercase tracking-[0.5px]">
              <TagIcon className="h-3.5 w-3.5 text-muted-foreground/70" />
              <span>Tags</span>
            </div>
            {tags.length > 0 && (
              <div className="flex flex-wrap gap-2.5 sm:gap-2">
                {tags.map((tag: string) => (
                  <Badge
                    key={tag}
                    variant="outline"
                    className="rounded-full px-4 py-2 sm:px-3.5 sm:py-1.5 text-sm sm:text-xs flex items-center gap-2 sm:gap-1.5 bg-background/50 border-border/50 shadow-[0_1px_3px_rgba(0,0,0,0.06)] hover:shadow-[0_2px_6px_rgba(0,0,0,0.1)] transition-all duration-200 hover:border-border hover:bg-background/70 min-h-[44px] sm:min-h-0 touch-manipulation"
                  >
                    <span className="font-medium">{tag}</span>
                    {editable && (
                      <button
                        type="button"
                        onClick={() => handleRemoveTag(tag)}
                        disabled={removeTagMutation.isPending}
                        className="ml-0.5 hover:bg-muted/80 active:bg-muted rounded-full p-2 sm:p-1.5 touch-manipulation min-h-[36px] min-w-[36px] sm:min-h-[32px] sm:min-w-[32px] flex items-center justify-center transition-all duration-150 active:scale-90"
                        aria-label={`Remove tag ${tag}`}
                      >
                        <X className="h-4 w-4 sm:h-3.5 sm:w-3.5 text-muted-foreground/70 hover:text-foreground transition-colors" />
                      </button>
                    )}
                  </Badge>
                ))}
              </div>
            )}
            {editable && (
              <div className="relative">
                <div className="flex gap-1.5">
                  <div className="relative flex-1">
                    <Input
                      ref={tagInputRef}
                      placeholder={uiLabels.addTag}
                      value={tagInput}
                      onChange={(e) => {
                        const value = e.target.value;
                        setTagInput(value);
                        // Keep dropdown open while user is editing
                        if (!showTagSuggestions) {
                          setShowTagSuggestions(true);
                        }
                      }}
                      onFocus={() => {
                        // Open dropdown on focus to show tag suggestions immediately
                        setShowTagSuggestions(true);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          if (tagSuggestions.length > 0 && showTagSuggestions) {
                            handleAddTag(tagSuggestions[0]);
                          } else {
                            handleAddTag();
                          }
                        } else if (e.key === "Escape") {
                          setShowTagSuggestions(false);
                        }
                      }}
                      className="h-12 sm:h-11 rounded bg-gray-50 dark:bg-gray-900/50 border-0 px-4 text-[17px] sm:text-base font-normal text-gray-900 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-800/50 transition-colors touch-manipulation min-h-[48px] sm:min-h-[44px] focus:ring-2 focus:ring-muted/50"
                      disabled={addTagMutation.isPending}
                    />
                    {/* Tag suggestions dropdown */}
                    {showTagSuggestions && (
                      <div
                        ref={tagSuggestionsRef}
                        className="absolute z-50 w-full mt-1.5 bg-background/95 backdrop-blur-md border border-border/50 rounded shadow-[0_4px_16px_rgba(0,0,0,0.12)] max-h-48 overflow-auto"
                      >
                        {/* Loading state */}
                        {isLoadingTags && (
                          <div className="px-4 py-3 text-xs text-muted-foreground/70">
                            Loading tags…
                          </div>
                        )}

                        {/* Suggestions list */}
                        {!isLoadingTags &&
                          tagSuggestions
                            .filter((suggestion) => !tags.includes(suggestion))
                            .slice(0, 10)
                            .map((suggestion) => (
                              <button
                                key={suggestion}
                                type="button"
                                onClick={() => handleAddTag(suggestion)}
                                className="w-full text-left px-4 py-3 sm:px-3 sm:py-2 text-sm sm:text-xs hover:bg-muted/80 active:bg-muted transition-all duration-150 touch-manipulation min-h-[44px] sm:min-h-0 active:scale-[0.98]"
                              >
                                {suggestion}
                              </button>
                            ))}

                        {/* Empty state - only show if user has typed 2+ chars */}
                        {!isLoadingTags &&
                          tagSuggestions.filter((s) => !tags.includes(s)).length === 0 &&
                          tagInput.length >= 2 && (
                            <div className="px-4 py-3 text-xs text-muted-foreground/70">
                              No matching tags
                            </div>
                          )}

                        {/* Error state - optional but helpful for debugging */}
                        {tagSuggestionsError && (
                          <div className="px-4 py-3 text-xs text-destructive/80">
                            Failed to load suggestions
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-12 sm:h-11 w-12 sm:w-11 rounded-full hover:bg-muted/80 active:bg-muted transition-all duration-200 touch-manipulation min-h-[48px] min-w-[48px] sm:min-h-[44px] sm:min-w-[44px] text-muted-foreground hover:text-foreground active:scale-95"
                    onClick={() => handleAddTag()}
                    disabled={!tagInput.trim() || addTagMutation.isPending}
                  >
                    <Plus className="h-5 w-5 sm:h-4 sm:w-4" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Divider */}
      {(tags.length > 0 || editable) && (
        <div className="border-t border-border/30" />
      )}
    </>
  );
}
